import { Observable } from 'rxjs';
import { PartnerModel } from './company.model';
import { LatestStatusResponse } from './status.response.model';
import { StatusBadgeType } from '@custom-components/general/status-chip/status-chip.component';

export type MeterRole = 'consumption' | 'production' | 'bidirectional';

export type MeterRoleOrigin = 'device' | 'partnerDefault' | 'fallback';

export function effectiveMeterRole(
  deviceMeterRole: MeterRole | undefined,
  partnerDefaultMeterRole: MeterRole | undefined,
): MeterRole {
  return deviceMeterRole ?? partnerDefaultMeterRole ?? 'bidirectional';
}

export const METER_ROLE_ICONS: Record<MeterRole, string> = {
  production: 'sunny',
  consumption: 'electrical_services',
  bidirectional: 'sync_alt',
};

export function mapDeviceStatusToBadgeStatus(status: string): StatusBadgeType {
  const s = status.toLowerCase();
  if (s.includes('offline') || s.includes('disconnected')) return 'error';
  if (s.includes('online') || s.includes('connected')) return 'success';
  return 'inactive';
}

// A device can only ever be manually set to one of these three - 'unknown_exhausted' is a
// read-only state the auto-detection job reaches on its own after 5 failed weekly checks.
export type ManualPhaseCount = '1' | '3' | 'unknown';

export type PhaseCount = ManualPhaseCount | 'unknown_exhausted';

export interface DeviceModel {
  // Fetched separately via DeviceService.getPhaseCount and merged in by DeviceStore -
  // GET /devices/{id} itself doesn't carry it (same as meterRole, no shared precedent).
  /** `null` means detection has never run for this device, which is a different state from
   * the 'unknown' the detection job sets once it has looked and stayed inconclusive. */
  phaseCount?: PhaseCount | null;
  // Comes from the same call: the device type fixes the phase count, so an update would 400.
  phaseCountFixed?: boolean;
  // Absent until the backend read-response is extended; consumers should fall back to
  // 'bidirectional' in the meantime.
  meterRole?: MeterRole;
  customerId: string;
  customerName: string;
  partnerOid: string;
  deviceConfigFile: string;
  deviceEnabled: string;
  deviceFirmwareVersion: string;
  deviceId: string;
  deviceImage: string;
  deviceInstallationStatus: string;
  deviceLDN: string;
  deviceName: string;
  devicePolling: string;
  devicePollingStatus: string;
  deviceStatus: string;
  // Consolidated online/offline verdict: recomputed against last_readout and registration state,
  // so it stops reading "online" once the readout has gone stale instead of only flipping on a
  // failed poll attempt. `deviceStatus` above is the raw, unconsolidated column and can disagree
  // with it — the table filters and displays by this field so the two stay in sync.
  deviceOnlineStatus: string;
  // The two status dimensions (RDT-1565): whether a fresh readout arrived, and where the device
  // stands in its installation. Absent from an API that has not shipped them yet.
  connectionState?: ConnectionStateEnum | null;
  installationState?: InstallationStateEnum | null;
  // Stable code, optionally suffixed with `:<code>`; set only when installationState is
  // `Installation failed`. Translate it with `installationFailureReasonKey`.
  installationFailureReason?: string | null;
  deviceSupportsTopology: boolean;
  lastReadout: string;
  deviceType: string;
  deviceTypeId: number;
  locationId: string;
  protocol: string;
  simActivationDate: string;
  simIccid: string;
  simImei: string;
  simImsi: string;
  simIpaddress: string;
  simMsisdn: string;
  simProvider: string;
  simStatus: string;
  // The last synced dm.sim_status row. GET /devices/{id} carries these, and refreshSim patches
  // them in-place afterwards; all null for a SIM the sync has never reached.
  simLifecycleStatus?: string | null;
  simDataRemainingMb?: number | null;
  simDataTotalMb?: number | null;
  simEndDate?: string | null;
  simLastSyncedAt?: string | null;
  lanIpaddress: string;
  lanStatus: string;
  internalDeviceStatus: LatestStatusResponse;
  partner?: Observable<PartnerModel | undefined>;
}

/** Response of POST /refresh-sim/{deviceId} (xmx-dpf-device-func) — both Xemex and the owning
 * partner's users may trigger this, mirroring the get_device auth fallback pattern. */
export interface SimStatusResult {
  iccid: string;
  sessionStatus: string;
  lifecycleStatus: string | null;
  dataRemainingMb: number | null;
  dataTotalMb: number | null;
  endDate: string | null;
  lastSyncedAt: string;
}

/** Response of POST /top-up-sim/{deviceId} and POST /extend-sim/{deviceId} (xmx-dpf-device-func)
 * — Xemex-only actions, both requiring the device's current iccid as a confirmation token. */
export interface SimActionResult {
  iccid: string;
  action: string;
  status: string;
  message: string | null;
}
export interface DeviceResponseModel {
  devices: DeviceModel[];
  numberOfItems: number;
}

export interface DeviceType {
  deviceTypeId: number;
  description: string;
  supportsTopology: boolean;
  protocol: string;
}
export interface DeviceTypeResponseModel {
  'device-types': DeviceType[];
  numberOfItems: number;
}

export interface StatusDevice {
  id: string;
  name: string;
}

export interface DevicePayloadModel {
  type?: string;
  pollingStatus?: string;
  onlineStatus?: string;
  connectionState?: string;
  installationState?: string;
  provisioning?: string;
  simStatus?: string;
  connectionType?: string;
  deviceType?: string;
  delivery_id?: string;
  firmwareVersion?: string;
  configurationFile?: string;
  partnerOid?: string;
  locationId?: string;
  deviceLDN?: string;
}

export interface DeviceFiltersModel extends DevicePayloadModel {
  limit: number;
  offset: number;
}

export interface StartReadoutData {
  polId: string;
  deviceId: string;
  error: string;
}

export interface StartReadoutResult {
  polId: string;
}

export interface ReadoutStatus {
  correlationId: string;
  initiatedBy: string;
  initiatedOn: string;
  status: string;
  error?: string;
}

export enum InstallationStatusEnum {
  Linked = 'Linked',
  VerificationFailed = 'Verification failed',
  Verified = 'Verified',
  ProvisioningFailed = 'Provisioning failed',
  Provisioning = 'Provisioning',
  Provisioned = 'Provisioned',
  ConnectionFailed = 'Connection failed',
  ConnectionTimedOut = 'Connection timed-out',
  ConnectionInitialized = 'Connection initialized',
  ConfigurationPending = 'Configuration pending',
  ConfigurationFailed = 'Configuration failed',
  Configured = 'Configured',
  ReprovisionDevice = 'Reprovision device',
  Registered = 'Registered',
}

export enum ConnectionStateEnum {
  Online = 'Online',
  Offline = 'Offline',
}

export enum InstallationStateEnum {
  Installed = 'Installed',
  ToBeInstalled = 'To be installed',
  InstallationFailed = 'Installation failed',
}

export enum SIMEnum {
  Online = 'Online',
  Offline = 'Offline',
}

export enum PollingDeviceEnum {
  NeverReceived = 'Never Received',
  ReceivedAsExpected = 'Received as Expected',
  PollingOverdue = 'Polling Overdue',
}

export enum ConnectionTypeEnum {
  SIMConnection = 'SIM Connection',
  LANConnection = 'LAN Connection',
  SIMAndLAN = 'LAN & SIM Connection',
  NoConnection = 'No Connection',
}

export interface Category {
  id: number;
  name: string;
}

export interface DeviceInterface {
  interfaceId: number;
  name: string;
}

export interface DeviceInterfaceWithContext extends DeviceInterface {
  context: DeviceInterfaceContext;
}

export interface Subdevice {
  deviceName: string;
  interfaces: DeviceInterfaceWithContext[];
}

interface DeviceConfiguration {
  fileId: number;
  description: string;
  devices: Subdevice[];
  dynamicParameters: DynamicParameter[];
}

interface DeviceConfigurationVersion extends DeviceConfiguration {
  versionId: number;
  installationDate: string;
  dynamicParameters: DynamicParameterWithValue[];
  interfaces: string[];
}

export interface DeviceConfigurationForDeviceChannel extends DeviceConfiguration {
  fileName: string;
  interfaces: DeviceInterface[];
}

export interface DeviceConfigurationUpdate {
  fileId: number;
  channelNumber: number;
  dynamicParameters: DynamicParameterWithValue[];
  interfaceIds: number[];
  context: Record<string, Record<string, DeviceInterfaceContext>>;
}

export interface DeviceConfigurationClear {
  channelNumber: number;
}

export interface DeviceChannel {
  channelNumber: number;
  description: string;
  type: string;
  deviceConfiguration: DeviceConfigurationVersion;
  settings: ChannelSetting[];
  categoryId: number;
  category?: string;
}

export interface ChannelSetting {
  dataType: string;
  displayName: string;
  name: string;
  selectValues: { name: string; value: string }[];
  unit: string;
  value: any;
}

export interface InitialConfiguration {
  fileId: number;
  versionId: number;
  description: string;
}

export interface DeviceChannelConfiguration {
  initialConfiguration?: InitialConfiguration;
  channels: DeviceChannel[];
  settings: ChannelSetting;
}

export interface DeviceConfigurationsForDeviceChannelResponse {
  deviceConfigurations: DeviceConfigurationForDeviceChannel[];
}

export interface DynamicParameter {
  name: string;
  deviceName: string;
}

export interface DynamicParameterWithValue extends DynamicParameter {
  value: string;
}

export interface ChannelUpdatePayload {
  settings: ChannelSettingUpdate[];
  deviceConfigurationUpdate: DeviceConfigurationUpdate;
}
export interface ChannelSettingUpdate {
  channelNumber: number;
  name: string;
  value: any;
}
export interface DeviceInterfaceContext {
  label: string | null;
  target: string | null;
}
export interface ConfigDispatchResult {
  status: string;
  error: string | null;
}

export interface InitialConfigFileOption {
  fileId: number;
  fileName: string;
  description: string;
}

export interface InitialConfigFilesForDevice {
  initialConfigurations: InitialConfigFileOption[];
}

export interface InitialConfigDispatch {
  initialConfigDispatchId: string;
}
export interface ConnectionTestResult {
  successful: boolean;
  networks: NetworkConnectionResult[];
}
export interface NetworkConnectionResult {
  name: string;
  successful: boolean;
  signal_strength: number;
}

export type DeviceStatus = {
  device_id: string;
  start_interval_time: string;
  end_interval_time?: string;
  status_id?: string;
  status_code?: string;
  status_names: string[];
  additional_information?: string;
};
